<!-- components/form/DropzoneImage.vue -->
<script setup lang="ts">
import { ref, computed } from 'vue'

type Accept = 'image/jpeg' | 'image/png' | 'image/webp' | 'image/svg+xml'

const props = withDefaults(
  defineProps<{
    /** Tamanho do quadrado do drop (px) */
    boxSize?: number
    /** Tamanho máximo em MB */
    maxMb?: number
    /** Abrir crop automaticamente ao selecionar */
    openCropOnSelect?: boolean
    /** Tipos aceitos */
    accept?: Accept[]
  }>(),
  {
    boxSize: 120,
    maxMb: 5,
    openCropOnSelect: true,
    accept: () => ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'],
  }
)

const emit = defineEmits<{
  /** Arquivo bruto selecionado/arrastado */
  (e: 'select', file: File): void
  /** Dispare isso para abrir seu CropDialog externamente */
  (e: 'open-crop', file: File): void
  /** Arquivo rejeitado (motivo) */
  (e: 'reject', reason: string): void
}>()

const isDragging = ref(false)
const inputEl = ref<HTMLInputElement | null>(null)

const acceptAttr = computed(() => props.accept.join(','))

function onBrowseClick() {
  inputEl.value?.click()
}

function validate(file: File) {
  const okType = props.accept.includes(file.type as Accept)
  if (!okType) {
    emit('reject', 'Formato não suportado.')
    return false
  }
  const okSize = file.size <= props.maxMb * 1024 * 1024
  if (!okSize) {
    emit('reject', `Arquivo maior que ${props.maxMb}MB.`)
    return false
  }
  return true
}

function handleFile(file?: File) {
  if (!file) return
  if (!validate(file)) return
  emit('select', file)
  if (props.openCropOnSelect) emit('open-crop', file)
}

function onInputChange(e: Event) {
  const files = (e.target as HTMLInputElement).files
  handleFile(files?.[0])
}

function onDrop(e: DragEvent) {
  e.preventDefault()
  isDragging.value = false
  const file = e.dataTransfer?.files?.[0]
  handleFile(file)
}
</script>

<template>
  <div class="dz-root">
    <div
      class="dz-area"
      :class="{ 'dz-dragging': isDragging }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop="onDrop"
    >
      <!-- Coluna esquerda: quadrado de upload -->
      <button
        class="dz-box"
        type="button"
        :style="{ width: boxSize + 'px', height: boxSize + 'px' }"
        @click="onBrowseClick"
      >
        <div class="dz-box-content">
          <!-- substitua por seu ícone se quiser -->
          <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M19 13v6H5v-6H3v8h18v-8h-2zM11 13.17V3h2v10.17l3.59-3.58L18 11l-6 6-6-6 1.41-1.41L11 13.17z"
            />
          </svg>
          <span class="dz-box-text">Clique ou arraste a<br />imagem aqui</span>
        </div>
      </button>

      <!-- Coluna direita: instruções -->
      <div class="dz-info">
        <h3 class="dz-title">Imagem de divulgação (opcional)</h3>
        <p class="dz-text">
          JPEG (recomendado), PNG, WebP e SVG (até {{ maxMb }}MB).<br />
          Mínimo recomendado 120x120px.<br />
          Ao enviar, recorte com o crop.
        </p>
      </div>
    </div>

    <!-- input "invisível" -->
    <input
      ref="inputEl"
      class="dz-input"
      type="file"
      :accept="acceptAttr"
      @change="onInputChange"
    />
  </div>
</template>

<style scoped>
.dz-root {
  width: 100%;
}

.dz-area {
  display: flex;
  gap: 24px;
  align-items: center;
  border: 1px dashed #d9d9d9; /* igual ao mock */
  border-radius: 10px;
  padding: 24px;
  background: #fff;
}

.dz-area.dz-dragging {
  outline: 2px solid #8cc63f; /* feedback ao arrastar */
  outline-offset: -2px;
}

.dz-box {
  display: inline-flex;
  border: 1px dashed #c8c8c8;
  border-radius: 8px;
  background: #fafafa;
  cursor: pointer;
  padding: 0;
}

.dz-box:focus {
  outline: 2px solid #8cc63f;
  outline-offset: 2px;
}

.dz-box-content {
  width: 100%;
  height: 100%;
  font-size: 12px;
  color: #7a7a7a;
  display: grid;
  place-items: center;
  text-align: center;
  gap: 8px;
}

.dz-box-content svg {
  fill: #8cc63f;
}

.dz-box-text {
  line-height: 1.25;
}

.dz-info {
  flex: 1 1 auto;
  min-width: 0;
}

.dz-title {
  margin: 0 0 8px 0;
  font-weight: 600;
  font-size: 18px; /* evita herdar H1 gigante */
  line-height: 1.3;
  color: #333;
}

.dz-text {
  margin: 0;
  font-size: 13px;
  color: #666;
}

.dz-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
@media (max-width: 768px) {
  .dz-area {
    flex-direction: column;
    align-items: stretch;
  }
  .dz-info {
    text-align: left;
  }
  .dz-title {
    font-size: 16px;
  }
}
</style>
